import React, { type ForwardedRef, type HTMLAttributes, type JSX } from 'react';
import styled, { css } from 'styled-components';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { focusRing } from '@/app/components/search/styles/mixins';

export type ImageSlideProps = {
  url: string;
  sources?: { srcSet: string; media?: string }[];
  loading?: 'eager' | 'lazy';
  overlay?: JSX.Element | null;
  containerRef?: ForwardedRef<HTMLDivElement>;
} & HTMLAttributes<HTMLDivElement>;

const ImageSlideOverlay = styled.div`
  > * {
    position: absolute;
  }
`;

/**
 * Single Slide of the gallery. Handles scroll snapping etc.
 */
function ImageSlide(props: ImageSlideProps) {
  const { url, sources, loading = 'lazy', overlay, containerRef, ...rest } = props;

  return (
    <div
      {...rest}
      ref={containerRef}
      css={css`
        flex: 0 0 calc(100vw - 32px);
        scroll-snap-align: center;
        scroll-snap-stop: always;
        position: relative;

        @media ${customCss.breakpoints.xs} {
          flex: 0 0 calc(100vw - 36px);
        }

        @media ${customCss.breakpoints.sm} {
          -webkit-transform: translate3d(0, 0, 0);
        }

        @media ${customCss.breakpoints.lg} {
          cursor: pointer;

          &:focus-visible::after {
            border-top-left-radius: 12px;
            border-bottom-left-radius: 12px;
          }
        }

        /* Drawn inside the slide: the scroll container clips anything outside it. */
        &:focus-visible {
          outline: none;
        }

        &:focus-visible::after {
          content: '';
          position: absolute;
          inset: 4px;
          pointer-events: none;
          ${focusRing}
        }
      `}
    >
      <Picture
        alt=""
        url={url}
        sources={sources}
        loading={loading}
        pictureCss={css`
          display: block;
          width: 100%;
          height: 100%;
        `}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1s;
          transform: scale3d(1, 1, 1);
          cursor: pointer;
          @media ${customCss.breakpoints.lg} {
            border-top-left-radius: 16px;
            border-bottom-left-radius: 16px;
          }
        `}
      />
      {overlay && <ImageSlideOverlay>{overlay}</ImageSlideOverlay>}
    </div>
  );
}

export { ImageSlide };
